/**
 * Runtime (Shuttle-side) interpretation of the `prompt-execution` strategy:
 * read the spec off the dispatched job's inputs and render the prompt.
 *
 * The substrate renders templates with a minimal Handlebars-like grammar:
 * `{{var}}` substitution and `{{#each items}}{{this}}{{/each}}`. No logic
 * helpers; products needing more use producer-side rendering.
 */

import type { PromptExecutionSpec } from './types';

export interface RenderedPromptExecution {
  spec: PromptExecutionSpec;
  prompt: string;
  artifacts: Array<{ label: string; payload: Record<string, unknown> }>;
}

/**
 * Read a `spec` input row off a dispatched job and return the parsed
 * `PromptExecutionSpec`. Throws on shape mismatch so the caller can fail-close.
 */
export function readPromptExecutionSpec(
  inputs: Array<{ label: string | null; payload: Record<string, unknown> }>,
): PromptExecutionSpec {
  const specRow = inputs.find((i) => i.label === 'spec');
  if (!specRow) throw new Error('prompt-execution: missing `spec` input row');
  const spec = (specRow.payload as { spec?: PromptExecutionSpec }).spec;
  if (!spec) throw new Error('prompt-execution: spec row payload missing `spec` field');
  if (spec.rendering !== 'producer' && spec.rendering !== 'subscriber') {
    throw new Error(`prompt-execution: invalid spec.rendering "${spec.rendering}"`);
  }
  if (typeof spec.outputLabel !== 'string' || spec.outputLabel.length === 0) {
    throw new Error('prompt-execution: spec.outputLabel must be a non-empty string');
  }
  if (typeof spec.outputSchema !== 'object' || spec.outputSchema === null) {
    throw new Error('prompt-execution: spec.outputSchema must be a JSON Schema object');
  }
  if (spec.workspace !== undefined) {
    // Shallow, namespace-agnostic check (mirrors how providerHints stays
    // opaque here): the bag and each namespace must be objects, and an empty
    // bag is an authoring error. Namespace shapes (e.g. `git`) are validated
    // by the executor that consumes them.
    const ws = spec.workspace;
    if (typeof ws !== 'object' || ws === null || Array.isArray(ws)) {
      throw new Error('prompt-execution: spec.workspace must be an object when present');
    }
    const namespaces = Object.entries(ws);
    if (namespaces.length === 0) {
      throw new Error('prompt-execution: spec.workspace must carry at least one namespace');
    }
    for (const [name, value] of namespaces) {
      if (typeof value !== 'object' || value === null || Array.isArray(value)) {
        throw new Error(`prompt-execution: spec.workspace.${name} must be an object`);
      }
    }
  }
  return spec;
}

export function renderPromptExecution(
  inputs: Array<{ label: string | null; payload: Record<string, unknown> }>,
): RenderedPromptExecution {
  const spec = readPromptExecutionSpec(inputs);
  const artifacts: Array<{ label: string; payload: Record<string, unknown> }> = [];
  for (const i of inputs) {
    if (i.label === 'artifacts') {
      const label = (i.payload as { label?: string }).label ?? '';
      artifacts.push({ label, payload: i.payload });
    }
  }

  let prompt = '';
  if (spec.rendering === 'producer') {
    const promptRow = inputs.find((i) => i.label === 'prompt');
    if (!promptRow) throw new Error("prompt-execution: rendering='producer' but no `prompt` input row");
    const text = (promptRow.payload as { text?: unknown }).text;
    if (typeof text !== 'string') {
      throw new Error('prompt-execution: prompt.payload.text must be a string');
    }
    prompt = text;
  } else {
    const templateRow = inputs.find((i) => i.label === 'template');
    if (!templateRow) throw new Error("prompt-execution: rendering='subscriber' but no `template` input row");
    const text = (templateRow.payload as { text?: unknown }).text;
    if (typeof text !== 'string') {
      throw new Error('prompt-execution: template.payload.text must be a string');
    }
    const varsRow = inputs.find((i) => i.label === 'vars');
    const vars = (varsRow?.payload ?? {}) as Record<string, unknown>;
    prompt = renderTemplate(text, vars);
  }

  return { spec, prompt, artifacts };
}

/**
 * Pull the provider-hints section matching an executor's capability id out of a
 * spec. Maps the kebab capability id (`anthropic-api`) to the camel hint key
 * (`anthropicApi`). Returns `{}` when absent.
 */
export function pickProviderHints(
  spec: PromptExecutionSpec,
  capabilityId: string,
): Record<string, unknown> {
  if (!spec.providerHints) return {};
  const key = capabilityId.replace(/-([a-z])/g, (_m, c: string) => c.toUpperCase());
  const matched = (spec.providerHints as Record<string, unknown>)[key];
  return matched && typeof matched === 'object' ? (matched as Record<string, unknown>) : {};
}

/**
 * Minimal `{{var}}` and `{{#each items}}...{{/each}}` rendering. No conditional
 * helpers, no nested member access; intentional. Exported for tests.
 */
export function renderTemplate(template: string, vars: Record<string, unknown>): string {
  const eachRe = /\{\{#each\s+([a-zA-Z_$][\w$]*)\s*\}\}([\s\S]*?)\{\{\/each\}\}/g;
  let out = template.replace(eachRe, (_match, name: string, body: string) => {
    const list = vars[name];
    if (!Array.isArray(list)) return '';
    return list.map((item) => body.replace(/\{\{\s*this\s*\}\}/g, String(item))).join('');
  });

  out = out.replace(/\{\{\s*([a-zA-Z_$][\w$]*)\s*\}\}/g, (_match, name: string) => {
    if (Object.prototype.hasOwnProperty.call(vars, name)) {
      const v = vars[name];
      return v === null || v === undefined ? '' : String(v);
    }
    return '';
  });

  return out;
}
